import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { z } from "zod";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { useTranslation } from "react-i18next";

const filterS1InvestStrategiesSchema = z.object({
  strategy_id_equals: z.string().optional(),
  strategy_name_contains: z.string().optional(),
  investment_strategy_contains: z.string().optional(),
  reference_indicator_contains: z.string().optional(),
  sfdr_classification_contains: z.string().optional(),
  entry_exit_fee_equals: z.string().optional(),
  max_management_fee_equals: z.string().optional(),
  minimum_subscription_contains: z.string().optional(),
  depositary_name_contains: z.string().optional(),
  valuator_name_contains: z.string().optional(),
  frequency_equals: z.string().optional(),
  feeder_fund_name_contains: z.string().optional(),
  fund_manager_name_contains: z.string().optional(),
  isr_score_equals: z.string().optional(),
  inception_date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  inception_date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterS1InvestStrategiesSchema = z.infer<
  typeof filterS1InvestStrategiesSchema
>;

const filterFields = filterS1InvestStrategiesSchema.keyof().enum;

export function FilterS1InvestStrategies() {
  const { t, i18n } = useTranslation("tables");
  const language = i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en";
  const tbl = Tables.S1_INVEST_STRATEGIES;

  const fields: Field[] = [
    {
      name: filterFields.strategy_id_equals,
      label: t(`${tbl}.searchbox.strategy_id_equals`),
      type: "input",
    },
    {
      name: filterFields.strategy_name_contains,
      label: t(`${tbl}.searchbox.strategy_name_contains`),
      type: "input",
    },
    {
      name: filterFields.investment_strategy_contains,
      label: t(`${tbl}.searchbox.investment_strategy_contains`),
      type: "input",
    },
    {
      name: filterFields.reference_indicator_contains,
      label: t(`${tbl}.searchbox.reference_indicator_contains`),
      type: "input",
    },
    {
      name: filterFields.sfdr_classification_contains,
      label: t(`${tbl}.searchbox.sfdr_classification_contains`),
      type: "input",
    },
    {
      name: filterFields.entry_exit_fee_equals,
      label: t(`${tbl}.searchbox.entry_exit_fee_equals`),
      type: "input",
    },
    {
      name: filterFields.max_management_fee_equals,
      label: t(`${tbl}.searchbox.max_management_fee_equals`),
      type: "input",
    },
    {
      name: filterFields.minimum_subscription_contains,
      label: t(`${tbl}.searchbox.minimum_subscription_contains`),
      type: "input",
    },
    {
      name: filterFields.depositary_name_contains,
      label: t(`${tbl}.searchbox.depositary_name_contains`),
      type: "input",
    },
    {
      name: filterFields.valuator_name_contains,
      label: t(`${tbl}.searchbox.valuator_name_contains`),
      type: "input",
    },
    {
      name: filterFields.frequency_equals,
      label: t(`${tbl}.searchbox.frequency_equals`),
      type: "select",
      useTranslation: true,
      client: Clients.S1_QUANTILIA,
      table_name: tbl,
      language,
      translate_key: "frequency",
    },
    {
      name: filterFields.feeder_fund_name_contains,
      label: t(`${tbl}.searchbox.feeder_fund_name_contains`),
      type: "input",
    },
    {
      name: filterFields.fund_manager_name_contains,
      label: t(`${tbl}.searchbox.fund_manager_name_contains`),
      type: "input",
    },
    {
      name: filterFields.isr_score_equals,
      label: t(`${tbl}.searchbox.isr_score_equals`),
      type: "input",
    },
    {
      name: "inception_date",
      type: "dateRange",
      label: t(`${tbl}.searchbox.inception_date`),
      from: { name: filterFields.inception_date_from },
      to: { name: filterFields.inception_date_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      client={Clients.S1_QUANTILIA}
      schema={filterS1InvestStrategiesSchema}
      tableName={`${tbl}`}
      customBuildPath={() => `${Clients.S1_QUANTILIA}/${tbl}`}
      fields={fields}
      columns={2}
    />
  );
}
